/**
 * src/middleware/AccountLockMiddleware.tsx
 * Sends non-admin users of a hub-only account to their hub, and keeps them there. Admins viewing
 * as a hub account are routed off "/" to the hub but can otherwise navigate freely.
 * Used as a layout route under AuthProvider in src/index.tsx: any path other than the
 * account's hub (including "/" right after login) redirects to it. Admin tokens
 * (is_admin) are not pinned, so the account switcher keeps working when viewing as a client.
 */
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { getToken } from 'helpers/localStorage';

// account id -> that account's hub. Accounts not listed use the normal app (/revenue etc.).
// Gemelli / JLI (297) is no longer pinned: it uses the normal app (Wholesale Accounts CRM)
// and lands there via the /revenue 403 redirect; /jli-reports stays reachable by direct link.
const ACCOUNT_HUBS: Record<string, string> = {
  '305': '/kendrick', // Kendrick Home publishing dashboard
};

const readToken = (): { account_id?: string | number; is_admin?: boolean } | null => {
  try {
    const token = getToken();
    if (!token) return null;
    return JSON.parse(atob(token.split('.')[1]));
  } catch {
    return null;
  }
};

const AccountLockMiddleware = () => {
  const location = useLocation();
  const payload = readToken();
  const hub = payload ? ACCOUNT_HUBS[String(payload.account_id)] : undefined;
  if (hub) {
    // clients are pinned to their hub; admins viewing as that account only get routed off "/"
    // (the post-login / account-switch landing), which would otherwise go to /revenue and crash
    // on an account with no available_pages rows
    const locked = !payload?.is_admin && location.pathname !== hub;
    const landing = payload?.is_admin && location.pathname === '/';
    if (locked || landing) return <Navigate to={hub} replace />;
  }
  return <Outlet />;
};

export default AccountLockMiddleware;
